/**
 * Tile-class mappings for the home page scorecard.
 *
 * Each helper returns the appropriate CSS-module class name for a tile's
 * current state. State thresholds mirror the canonical source surface for
 * each stat (see comments below), while colors use the scorecard's shared
 * soft palette defined in tiles.module.css.
 */

type StylesMap = Record<string, string>

// Duplicated from Streaks.module.css:30-46 and StreaksWidget.tsx
// (statusClass = styles[streak.status.toLowerCase()]).
// Keep in sync if streak state colors change.
export function getStreakTileClass(
  status: string | undefined,
  styles: StylesMap,
): string | undefined {
  switch (status) {
    case 'OnTrack':
      return styles.streakOnTrack
    case 'Hit':
      return styles.streakHit
    case 'AtRisk':
      return styles.streakAtRisk
    case 'Started':
    case 'Missed':
      return styles.tileNeutral
    default:
      return styles.tileNeutral
  }
}

// Week Streak ring fill (no backend change): no streak / at risk = 0,
// on track = 0.5, hit = 1. "Ahead" — OnTrack with periodsAhead > 0 — fills the
// whole ring like a hit; plain OnTrack stays half.
export function getStreakFill(
  status: string | undefined,
  periodsAhead = 0,
): number {
  switch (status) {
    case 'Hit':
      return 1
    case 'OnTrack':
      return periodsAhead > 0 ? 1 : 0.5
    default:
      return 0
  }
}

// Thresholds duplicated from commentScoreTheme.ts (getScoreLevel,
// SCORE_THRESHOLDS); keep in sync. Uses the neutral variant for low to match
// /community's CommentScoreMetric (getScoreColors(score, 'neutral')), but
// colored states use the scorecard's softer palette (see tiles.module.css)
// rather than /community's stronger borders.
export function getCommentScoreTileClass(
  score: number,
  styles: StylesMap,
): string {
  if (score > 80) return styles.scoreHigh
  if (score > 60) return styles.scoreMidHigh
  if (score > 40) return styles.scoreMedium
  if (score > 20) return styles.scoreMidLow
  return styles.tileNeutral
}

// %-based thresholds (see tiles.module.css). Expects the completion
// percentage (0–100): 0 neutral, 1–33 low, 34–66 mid, 67–99 high, 100 complete.
export function getPostingGoalsTileClass(
  percent: number,
  styles: StylesMap,
): string {
  if (percent <= 0) return styles.tileNeutral
  if (percent <= 33) return styles.goalsLow
  if (percent <= 66) return styles.goalsMid
  if (percent < 100) return styles.goalsHigh
  return styles.goalsComplete
}
